import { STATE_KEYS } from './StateConstants.js';
import { EquipmentDataLoader } from '../data/EquipmentDataLoader.js';

/**
 * Deck and Equipment state management module.
 * Handles card deck operations and equipment management.
 * Receives parent GameStateManager reference for state access and notifications.
 */
export class DeckState {
  constructor(parent) {
    this.parent = parent;
  }

  get state() {
    return this.parent.state;
  }

  _notify(key) {
    this.parent._notify(key);
  }

  // ==========================================
  // DECK
  // ==========================================

  getOwnedCards() {
    return this.state.deck.ownedCards;
  }

  getActiveCards() {
    return this.state.deck.ownedCards.filter(c => c.active > 0);
  }

  getTotalActiveCardCount() {
    return this.state.deck.ownedCards.reduce((sum, c) => sum + c.active, 0);
  }

  getEffectiveMaxActiveCards() {
    let base = this.state.deck.maxActiveCards;
    const activeEquip = this.getActiveEquipment();

    if (!EquipmentDataLoader.isLoaded()) {
      return base;
    }

    for (const equip of activeEquip) {
      const equipData = EquipmentDataLoader.getEquipmentByIdSync(equip.equipmentId);
      if (equipData?.modifiers?.maxActiveCards) {
        base += equipData.modifiers.maxActiveCards;
      }
    }

    return base;
  }

  getEquipmentModifiers() {
    const modifiers = {
      maxActiveCards: 0,
      startingMana: 0,
      startingGold: 0,
      startingDefense: 0,
      bonusDamage: 0,
      unitHealth: 0,
      extraCardsPerTurn: 0,
      maxActiveEquipment: 0,
      maxSpirit: 0
    };

    if (!EquipmentDataLoader.isLoaded()) {
      return modifiers;
    }

    const activeEquip = this.getActiveEquipment();
    for (const equip of activeEquip) {
      const equipData = EquipmentDataLoader.getEquipmentByIdSync(equip.equipmentId);
      if (!equipData?.modifiers) continue;

      for (const [key, value] of Object.entries(equipData.modifiers)) {
        if (typeof value === 'boolean') {
          modifiers[key] = modifiers[key] || value;
        } else if (typeof value === 'number') {
          modifiers[key] = (modifiers[key] || 0) + value;
        }
      }
    }

    return modifiers;
  }

  getEffectiveCardsPerTurn() {
    let base = this.state.deck.cardsPerTurn;
    const activeEquip = this.getActiveEquipment();

    if (!EquipmentDataLoader.isLoaded()) {
      return base;
    }

    for (const equip of activeEquip) {
      const equipData = EquipmentDataLoader.getEquipmentByIdSync(equip.equipmentId);
      if (equipData?.modifiers?.extraCardsPerTurn) {
        base += equipData.modifiers.extraCardsPerTurn;
      }
    }

    return base;
  }

  getEffectiveMaxEquipmentSlots() {
    let base = this.state.equipment.maxActiveSlots;
    const activeEquip = this.getActiveEquipment();

    if (!EquipmentDataLoader.isLoaded()) {
      return base;
    }

    for (const equip of activeEquip) {
      const equipData = EquipmentDataLoader.getEquipmentByIdSync(equip.equipmentId);
      if (equipData?.modifiers?.maxActiveEquipment) {
        base += equipData.modifiers.maxActiveEquipment;
      }
    }

    return base;
  }

  getEffectiveMaxSpirit() {
    let base = this.state.resources.maxSpirit;
    const activeEquip = this.getActiveEquipment();

    if (!EquipmentDataLoader.isLoaded()) {
      return base;
    }

    for (const equip of activeEquip) {
      const equipData = EquipmentDataLoader.getEquipmentByIdSync(equip.equipmentId);
      if (equipData?.modifiers?.maxSpirit) {
        base += equipData.modifiers.maxSpirit;
      }
    }

    return base;
  }

  addCard(cardId, count = 1) {
    const existing = this.state.deck.ownedCards.find(c => c.cardId === cardId);

    if (existing) {
      existing.count += count;
    } else {
      this.state.deck.ownedCards.push({ cardId, count, active: 0 });
    }
    this._notify(STATE_KEYS.DECK);
  }

  setCardActiveCount(cardId, activeCount) {
    const card = this.state.deck.ownedCards.find(c => c.cardId === cardId);

    if (!card) return false;

    const newActiveCount = Math.max(0, Math.min(activeCount, card.count));
    const activeChange = newActiveCount - card.active;
    const currentTotalActive = this.getTotalActiveCardCount();

    if (activeChange > 0 && currentTotalActive + activeChange > this.state.deck.maxActiveCards) {
      console.warn('Active card limit reached');
      return false;
    }

    card.active = newActiveCount;
    this.parent.save();
    this._notify(STATE_KEYS.DECK);
    this._notify(STATE_KEYS.ACTIVE_CARDS);
    return true;
  }

  drawRandomActiveCards(count) {
    const activeCards = this.getActiveCards();
    const deployedCards = this.parent.battle.getDeployedCards();
    const cardsInHand = this.parent.battle.getCardsInHand();
    const pool = [];

    for (const card of activeCards) {
      // Count how many of this card are currently deployed as units/buildings
      const deployedCount = deployedCards.filter(id => id === card.cardId).length;
      // Count how many are already in hand
      const inHandCount = cardsInHand.filter(id => id === card.cardId).length;
      const availableCount = card.active - deployedCount - inHandCount;

      for (let i = 0; i < availableCount; i++) {
        pool.push(card.cardId);
      }
    }

    // Fisher-Yates shuffle
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    return pool.slice(0, Math.min(count, pool.length));
  }

  // ==========================================
  // DECK SLOTS
  // ==========================================

  _ensureDeckSlotsExist() {
    if (this.state.deck.deckSlots === undefined) {
      // Migration: create slots from current active cards config
      const currentConfig = {};
      for (const card of this.state.deck.ownedCards) {
        if (card.active > 0) {
          currentConfig[card.cardId] = card.active;
        }
      }
      this.state.deck.deckSlots = [currentConfig, {}, {}, {}, {}];
      this.state.deck.activeDeckSlot = 0;
    }
  }

  getActiveDeckSlot() {
    this._ensureDeckSlotsExist();
    return this.state.deck.activeDeckSlot;
  }

  getDeckSlots() {
    this._ensureDeckSlotsExist();
    return this.state.deck.deckSlots;
  }

  getCurrentSlotConfig() {
    this._ensureDeckSlotsExist();
    return this.state.deck.deckSlots[this.state.deck.activeDeckSlot];
  }

  saveCurrentConfigToSlot() {
    this._ensureDeckSlotsExist();
    const config = {};
    for (const card of this.state.deck.ownedCards) {
      if (card.active > 0) {
        config[card.cardId] = card.active;
      }
    }
    this.state.deck.deckSlots[this.state.deck.activeDeckSlot] = config;
  }

  switchDeckSlot(slotIndex) {
    this._ensureDeckSlotsExist();
    
    if (slotIndex < 0 || slotIndex > 4) {
      console.warn('Invalid deck slot index:', slotIndex);
      return false;
    }

    if (slotIndex === this.state.deck.activeDeckSlot) {
      return true;
    }

    // Save current active configuration to current slot
    this.saveCurrentConfigToSlot();

    // Load new slot configuration
    const newConfig = this.state.deck.deckSlots[slotIndex] || {};

    // Apply new configuration to ownedCards
    for (const card of this.state.deck.ownedCards) {
      card.active = newConfig[card.cardId] || 0;
    }

    this.state.deck.activeDeckSlot = slotIndex;
    this.parent.save();
    this._notify(STATE_KEYS.DECK);
    this._notify(STATE_KEYS.DECK_SLOTS);
    this._notify(STATE_KEYS.ACTIVE_CARDS);
    return true;
  }

  // ==========================================
  // EQUIPMENT
  // ==========================================

  getOwnedEquipment() {
    return this.state.equipment.ownedEquipment;
  }

  getActiveEquipment() {
    return this.state.equipment.ownedEquipment.filter(e => e.active);
  }

  addEquipment(equipmentId) {
    const existing = this.state.equipment.ownedEquipment.find(
      e => e.equipmentId === equipmentId
    );

    if (!existing) {
      this.state.equipment.ownedEquipment.push({ equipmentId, active: false });
      // Equipment management unlocks with the first owned item, no matter the
      // source (battle reward, trader, story gift, NPC call) — the memory is
      // granted here so no acquisition path can miss it.
      if (!this.parent.hasMemory('first_equipment')) {
        this.parent.addMemory('first_equipment');
      }
      this._notify(STATE_KEYS.EQUIPMENT);
      return true;
    }
    return false;
  }

  setEquipmentActive(equipmentId, active) {
    const equipment = this.state.equipment.ownedEquipment.find(
      e => e.equipmentId === equipmentId
    );

    if (!equipment) return false;

    if (active && !equipment.active) {
      if (this.getActiveEquipment().length >= this.getEffectiveMaxEquipmentSlots()) {
        console.warn('Active equipment limit reached');
        return false;
      }
    }

    // Check if this equipment affects maxSpirit before activation change
    let affectsMaxSpirit = false;
    let isToggleable = false;
    if (EquipmentDataLoader.isLoaded()) {
      const equipData = EquipmentDataLoader.getEquipmentByIdSync(equipmentId);
      if (equipData?.modifiers?.maxSpirit) {
        affectsMaxSpirit = true;
      }
      isToggleable = equipData?.toggleable || false;
    }

    equipment.active = active;
    
    // Handle toggleable equipment loading state
    if (isToggleable) {
      if (active) {
        // Add loading state when activating toggleable equipment
        this._addEquipmentLoading(equipmentId, 1);
        this.setEquipmentToggled(equipmentId, true);
      } else {
        // Remove loading state when deactivating (prevents orphaned loading entries)
        this._removeEquipmentLoading(equipmentId);
      }
    }
    
    this._notify(STATE_KEYS.EQUIPMENT);
    this._notify(STATE_KEYS.ACTIVE_EQUIPMENT);
    
    // If maxSpirit changed, notify SPIRIT so UI updates
    if (affectsMaxSpirit) {
      this._notify(STATE_KEYS.SPIRIT);
    }
    
    return true;
  }

  // ==========================================
  // EQUIPMENT TOGGLE (for toggleable equipment)
  // ==========================================

  getToggledEquipment() {
    return this.state.equipment.toggledEquipment || [];
  }

  isEquipmentToggled(equipmentId) {
    const toggled = this.getToggledEquipment();
    return toggled.includes(equipmentId);
  }

  toggleEquipment(equipmentId) {
    if (!this.state.equipment.toggledEquipment) {
      this.state.equipment.toggledEquipment = [];
    }

    const index = this.state.equipment.toggledEquipment.indexOf(equipmentId);
    if (index === -1) {
      this.state.equipment.toggledEquipment.push(equipmentId);
    } else {
      this.state.equipment.toggledEquipment.splice(index, 1);
    }
    
    this._notify(STATE_KEYS.EQUIPMENT);
    this.parent.save();
    return this.isEquipmentToggled(equipmentId);
  }

  setEquipmentToggled(equipmentId, toggled) {
    if (!this.state.equipment.toggledEquipment) {
      this.state.equipment.toggledEquipment = [];
    }

    const currentlyToggled = this.isEquipmentToggled(equipmentId);
    if (toggled && !currentlyToggled) {
      this.state.equipment.toggledEquipment.push(equipmentId);
      this._notify(STATE_KEYS.EQUIPMENT);
      this.parent.save();
    } else if (!toggled && currentlyToggled) {
      const index = this.state.equipment.toggledEquipment.indexOf(equipmentId);
      this.state.equipment.toggledEquipment.splice(index, 1);
      this._notify(STATE_KEYS.EQUIPMENT);
      this.parent.save();
    }
    return toggled;
  }

  getToggleableEquipmentModifiers() {
    // Get modifiers only from equipment that is both active AND toggled AND not loading
    const modifiers = {
      fogRadiusBonus: 0,
      radarEnabled: false
    };

    if (!EquipmentDataLoader.isLoaded()) {
      return modifiers;
    }

    const activeEquip = this.getActiveEquipment();
    for (const equip of activeEquip) {
      const equipData = EquipmentDataLoader.getEquipmentByIdSync(equip.equipmentId);
      if (!equipData?.toggleable || !equipData?.modifiers) continue;
      
      // Skip equipment that is still loading
      if (this.isEquipmentLoading(equip.equipmentId)) continue;
      
      // Auto-toggle if active but not yet toggled (legacy save migration)
      if (!this.isEquipmentToggled(equip.equipmentId)) {
        this.setEquipmentToggled(equip.equipmentId, true);
      }

      for (const [key, value] of Object.entries(equipData.modifiers)) {
        if (typeof value === 'boolean') {
          modifiers[key] = modifiers[key] || value;
        } else if (typeof value === 'number') {
          modifiers[key] = (modifiers[key] || 0) + value;
        }
      }
    }

    return modifiers;
  }

  // ==========================================
  // EQUIPMENT LOADING (turn-based activation)
  // ==========================================

  getLoadingEquipment() {
    return this.state.equipment.loadingEquipment || [];
  }

  isEquipmentLoading(equipmentId) {
    const loading = this.getLoadingEquipment();
    return loading.some(entry => entry.equipmentId === equipmentId);
  }

  _addEquipmentLoading(equipmentId, turnsRemaining = 1) {
    if (!this.state.equipment.loadingEquipment) {
      this.state.equipment.loadingEquipment = [];
    }
    
    // Remove existing entry if any
    this._removeEquipmentLoading(equipmentId);
    
    this.state.equipment.loadingEquipment.push({ equipmentId, turnsRemaining });
    this._notify(STATE_KEYS.EQUIPMENT);
  }

  _removeEquipmentLoading(equipmentId) {
    if (!this.state.equipment.loadingEquipment) return;
    
    const index = this.state.equipment.loadingEquipment.findIndex(
      entry => entry.equipmentId === equipmentId
    );
    if (index !== -1) {
      this.state.equipment.loadingEquipment.splice(index, 1);
    }
  }

  decrementEquipmentLoading() {
    const loading = this.getLoadingEquipment();
    if (loading.length === 0) return;
    
    // Decrement all loading counters and remove completed ones
    const remaining = [];
    for (const entry of loading) {
      entry.turnsRemaining -= 1;
      if (entry.turnsRemaining > 0) {
        remaining.push(entry);
      }
    }
    
    this.state.equipment.loadingEquipment = remaining;
    this._notify(STATE_KEYS.EQUIPMENT);
    this.parent.save();
  }
}
